import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Trash2, RefreshCw } from 'lucide-react';
import { fetchPurgePreview, purgeExercises } from '../../services/api';

const EXERCISE_TYPES = [
  { key: 'vocabulary', label: 'Vocabulary Fill-in', table: 'vocabulary', color: '#28a745' },
  { key: 'grammar_fillin', label: 'Grammar Fill-in', table: 'grammar_fillin_exercises', color: '#6f42c1' },
  { key: 'speaking', label: 'Speaking Exercises', table: 'speaking_exercises', color: '#dc3545' },
  { key: 'conversations', label: 'Conversations', table: 'conversations', color: '#0d6efd', extraTable: 'conversation_qa' },
  { key: 'writing', label: 'Writing Exercises', table: 'writing_exercises', color: '#fd7e14' },
];

const DatabaseManager = ({ onPurgeComplete }) => {
  const [counts, setCounts] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [purgeUserProgress, setPurgeUserProgress] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [purging, setPurging] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPurgePreview();
      setCounts(data.counts);
    } catch (err) {
      setError('Failed to load database counts: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const toggleType = (key) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setConfirming(false);
    setConfirmText('');
    setResult(null);
  };

  const selectAll = () => {
    setSelectedTypes(new Set(EXERCISE_TYPES.map(t => t.key)));
    setConfirming(false);
    setResult(null);
  };

  const deselectAll = () => {
    setSelectedTypes(new Set());
    setConfirming(false);
    setResult(null);
  };

  const handlePurge = async () => {
    if (confirmText !== 'PURGE') return;
    try {
      setPurging(true);
      setError(null);
      setResult(null);
      const data = await purgeExercises([...selectedTypes], purgeUserProgress);
      setResult(data);
      setConfirming(false);
      setConfirmText('');
      setSelectedTypes(new Set());
      setPurgeUserProgress(false);
      await loadCounts();
      if (onPurgeComplete) onPurgeComplete();
    } catch (err) {
      setError('Purge failed: ' + err.message);
    } finally {
      setPurging(false);
    }
  };

  const totalSelected = EXERCISE_TYPES
    .filter(t => selectedTypes.has(t.key))
    .reduce((sum, t) => {
      let count = counts?.[t.table] || 0;
      if (t.extraTable) count += counts?.[t.extraTable] || 0;
      return sum + count;
    }, 0);

  const progressCount = (counts?.exercise_attempts || 0) + (counts?.exercise_progress || 0);

  return (
    <div>
      {/* Warning Banner */}
      <div style={{
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderLeft: '4px solid #dc2626',
        borderRadius: '8px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
      }}>
        <AlertTriangle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
            Destructive Operation
          </div>
          <div style={{ color: '#7f1d1d', fontSize: '14px', lineHeight: '1.5' }}>
            Purging exercises will permanently delete all selected exercise data from the database.
            This action cannot be undone. Seeded data will be restored on the next application restart.
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#dc2626',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '24px',
        }}>
          <div style={{ fontWeight: 600, color: '#166534', marginBottom: '8px' }}>
            Purge completed successfully
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(result.deleted).map(([table, count]) => (
              <span key={table} style={{
                backgroundColor: '#dcfce7',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#166534',
              }}>
                {table}: {count} deleted
              </span>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading database counts...
        </div>
      ) : (
        <>
          {/* Exercise Type Selection */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                Select Exercise Types to Purge
              </h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={selectAll} style={{
                  padding: '6px 12px', fontSize: '13px', border: '1px solid #d1d5db',
                  borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', color: '#374151',
                }}>Select All</button>
                <button onClick={deselectAll} style={{
                  padding: '6px 12px', fontSize: '13px', border: '1px solid #d1d5db',
                  borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', color: '#374151',
                }}>Deselect All</button>
                <button onClick={loadCounts} style={{
                  padding: '6px 12px', fontSize: '13px', border: '1px solid #d1d5db',
                  borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer', color: '#374151',
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}>
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {EXERCISE_TYPES.map(({ key, label, table, color, extraTable }) => {
                const count = counts?.[table] || 0;
                const extraCount = extraTable ? (counts?.[extraTable] || 0) : 0;
                const isSelected = selectedTypes.has(key);
                return (
                  <label key={key} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', borderRadius: '8px', cursor: 'pointer',
                    border: isSelected ? `2px solid ${color}` : '2px solid #e5e7eb',
                    backgroundColor: isSelected ? `${color}10` : 'white',
                    transition: 'all 0.15s ease',
                  }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleType(key)}
                      style={{ width: '18px', height: '18px', accentColor: color, cursor: 'pointer' }}
                    />
                    <span style={{
                      width: '4px', height: '32px', borderRadius: '2px', backgroundColor: color,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, color: '#374151', fontSize: '15px' }}>{label}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        {count} records
                        {extraTable && ` + ${extraCount} Q&A pairs`}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* User Progress Toggle */}
          <div style={{
            padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb', marginBottom: '24px',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={purgeUserProgress}
                onChange={(e) => { setPurgeUserProgress(e.target.checked); setConfirming(false); setResult(null); }}
                style={{ width: '18px', height: '18px', accentColor: '#dc2626', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: 500, color: '#374151', fontSize: '15px' }}>
                  Also purge my progress data
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  Exercise Attempts ({counts?.exercise_attempts || 0} records) + Exercise Progress ({counts?.exercise_progress || 0} records)
                </div>
              </div>
            </label>
          </div>

          {/* Purge Button / Confirmation */}
          {!confirming ? (
            <button
              onClick={() => { setConfirming(true); setConfirmText(''); }}
              disabled={selectedTypes.size === 0 && !purgeUserProgress}
              style={{
                padding: '12px 24px',
                backgroundColor: (selectedTypes.size === 0 && !purgeUserProgress) ? '#9ca3af' : '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: (selectedTypes.size === 0 && !purgeUserProgress) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Trash2 size={16} />
              Purge Selected ({totalSelected + (purgeUserProgress ? progressCount : 0)} records)
            </button>
          ) : (
            <div style={{
              padding: '20px',
              backgroundColor: '#fef2f2',
              border: '2px solid #dc2626',
              borderRadius: '8px',
            }}>
              <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '8px', fontSize: '15px' }}>
                Confirm Purge
              </div>
              <div style={{ color: '#7f1d1d', fontSize: '14px', marginBottom: '12px' }}>
                This will permanently delete {totalSelected + (purgeUserProgress ? progressCount : 0)} records.
                Type <strong>PURGE</strong> to confirm.
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder='Type "PURGE" to confirm'
                  style={{
                    padding: '8px 12px', border: '1px solid #fca5a5', borderRadius: '6px',
                    fontSize: '14px', width: '220px',
                  }}
                  autoFocus
                />
                <button
                  onClick={handlePurge}
                  disabled={confirmText !== 'PURGE' || purging}
                  style={{
                    padding: '8px 20px',
                    backgroundColor: confirmText === 'PURGE' && !purging ? '#dc2626' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: confirmText === 'PURGE' && !purging ? 'pointer' : 'not-allowed',
                  }}
                >
                  {purging ? 'Purging...' : 'Confirm Purge'}
                </button>
                <button
                  onClick={() => { setConfirming(false); setConfirmText(''); }}
                  style={{
                    padding: '8px 16px', backgroundColor: 'white', color: '#374151',
                    border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DatabaseManager;
